import React from 'react';
import { useDispatch } from 'react-redux';
import { t } from 'Src/i18n';
import { useAppContext } from 'Context/ReactAppContext';
import {
  deleteAdditionalServices,
  addAdditionalService,
} from 'Store/booking/proposals/proposals.action';
import { TransfersSection } from 'Components/widgets/package/transfers-section/TransfersSection';
import { TRANSFER_TYPE_ENUM } from 'Views/booking/constants';
import {
  getProposalIdsAndOverloadForAdditionalServiceQuery,
  isTransferIncludedInProposal,
} from 'Utils/transport/transportUtils';
import { trackErrorOnAddService } from 'Views/booking/BookingEngine.utils';
import { useBETracking } from 'Tracking/tracking-api/useBETracking';
import { Tag } from 'Components/frameworks/tag/Tag';
import { Title } from 'Components/frameworks/title/Title';
import { TransfersInfos } from './TransfersInfos';
type TransfersViewProps = {
  proposals: Array<Proposal>;
  selectedProposal: Proposal;
  resort: Product;
};
export const TransfersView = ({ proposals, selectedProposal, resort }: TransfersViewProps) => {
  const appContext = useAppContext();
  const dispatch = useDispatch();
  const track = useBETracking();
  const availableTransfers = selectedProposal.additionalServices.transfers ?? [];
  const selectedTransfers = (selectedProposal.additionalServices.transfers || []).filter(
    (transfer) => transfer.pricingInfo.isFullyRented,
  );
  const isTransferIncluded = isTransferIncludedInProposal(selectedProposal);
  const firstTransferDirection = availableTransfers?.[0]?.directionType;

  function getTransferTitleKey() {
    if (isTransferIncluded) {
      return 'common.transport.transfer.withTransferLabel';
    }
    if (firstTransferDirection === TRANSFER_TYPE_ENUM.INBOUND) {
      return 'common.transport.transfer.inbound';
    }
    if (firstTransferDirection === TRANSFER_TYPE_ENUM.OUTBOUND) {
      return 'common.transport.transfer.outbound';
    }
    return 'common.transport.transfer.transferOnDemand';
  }

  const [hasSelectedTransfer, setHasSelectedTransfer] = React.useState(false);

  const onAddTransfer = (transfer: UpsellTransfer) => {
    const { proposalIds, overloadForSpecificProposalIds } =
      getProposalIdsAndOverloadForAdditionalServiceQuery(proposals, transfer);
    const addTransferAction = addAdditionalService({
      proposalIds,
      serviceId: transfer.pricingInfo.id,
      schedules: transfer.pricingInfo.schedules.map(({ startDate, endDate }) => ({
        startDate,
        endDate,
      })),
      attendeeIds: transfer.pricingInfo.schedules[0].attendees.map(({ id }) => id),
      overloadForSpecificProposalIds,
      appContext,
    });
    return dispatch(addTransferAction).catch((error) =>
      trackErrorOnAddService(
        error,
        track({
          actionGA: 'transport',
        }),
        'TRANSFER_NOT_FOUND',
      ),
    );
  };

  const onRemoveTransfer = (transfer: UpsellTransfer) => {
    const { overloadForSpecificProposalIds } = getProposalIdsAndOverloadForAdditionalServiceQuery(
      proposals,
      transfer,
    );
    return dispatch(
      deleteAdditionalServices({
        serviceIds: [transfer.productInfo.id],
        attendeeIds: transfer.pricingInfo.schedules[0].attendees.map((attendee) => attendee.id),
        overloadForSpecificProposalIds,
        appContext,
      }),
    );
  };

  return (
    <div>
      <Tag
        className={
          isTransferIncluded
            ? 'mb-10 border-green bg-green text-white'
            : 'mb-10 border-sienna bg-sienna text-white'
        }
      >
        {isTransferIncluded ? t('booking.common.included') : t('booking.confirmation.onDemand')}
      </Tag>
      <Title className="mb-20" theme="title-brand-n0">
        {t(getTransferTitleKey())}
      </Title>
      <TransfersSection
        isBE
        availableTransfers={availableTransfers}
        selectedTransfers={selectedTransfers}
        addTransfer={onAddTransfer}
        removeTransfer={onRemoveTransfer}
        setHasSelectedTransfer={setHasSelectedTransfer}
      />
      <TransfersInfos
        hasSelectedTransfer={hasSelectedTransfer}
        resort={resort}
        selectedProposal={selectedProposal}
      />
    </div>
  );
};
